import React, { useState } from 'react'

import SettingsStoreInstance, {
  useSettingsStore,
  type SettingsStoreState,
} from '../../stores/settings'
import DesktopSettingsSwitch from './DesktopSettingsSwitch'
import MicrophoneTest from './MicrophoneTest'
import SettingsHeading from './SettingsHeading'
import SettingsSeparator from './SettingsSeparator'
import useTranslationFunction from '../../hooks/useTranslationFunction'
import SettingsSwitch from './SettingsSwitch'
import { runtime } from '@deltachat-desktop/runtime-interface'
import useDialog from '../../hooks/dialog/useDialog'
import AlertDialog from '../dialogs/AlertDialog'
import Dialog, {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FooterActionButton,
  FooterActions,
} from '../Dialog'
import { DialogProps } from '../../contexts/DialogContext'
import { getLogger } from '@deltachat-desktop/shared/logger'
import { DeltaInput } from '../Login-Styles'
import SettingsSelector from './SettingsSelector'
import { defaultAppStoreBaseUrl } from '@deltachat-desktop/shared/state'

const log = getLogger('ExperimentalFeatures')

export function ExperimentalFeatures() {
  const tx = useTranslationFunction()
  const settingsStore = useSettingsStore()[0]
  const { openDialog } = useDialog()
  // Remount key for the microphone test — see where it is used below.
  const [micTestRun, setMicTestRun] = useState(0)

  const showExperimentalInfoDialog = async (
    settingsKey: keyof Pick<
      SettingsStoreState['desktopSettings'],
      'enableOnDemandLocationStreaming'
    >,
    updatedValue: boolean
  ) => {
    if (!updatedValue) {
      return
    }
    let message: string
    // The strings are copy-pasted from
    // https://github.com/deltachat/deltachat-android/blob/2385b236c7ed9eb0e26ef819d8274936877b7023/src/main/java/org/thoughtcrime/securesms/preferences/AdvancedPreferenceFragment.java

    switch (settingsKey) {
      case 'enableOnDemandLocationStreaming':
        message =
          'Thanks for trying out "On-Demand Location Streaming"\n\n' +
          '• If enabled you will find a map icon above the message list, which opens a map with shared locations of your contacts' +
          '\n\n• Sharing your own location is only available in mobile clients'
        break
    }

    message +=
      '\n\n• If you want to quit the experimental feature, you can disable it at "Settings / Experimental features"'
    openDialog(AlertDialog, {
      message,
      confirmLabel: tx('ok'),
    })
  }

  // grouped by where the feature shows up, so related toggles sit together
  return (
    <>
      <SettingsHeading>Composer</SettingsHeading>
      <DesktopSettingsSwitch
        settingsKey='experimentalCompletionMenu'
        label='Emoji completion menu'
        description='Type a colon and a name in the composer (e.g. :smile) to pick an emoji from a pop-up menu. On by default.'
        forkAdded
      />
      <DesktopSettingsSwitch
        settingsKey='linkPreviewSuggestions'
        label='Link-preview suggestions'
        description='When your message contains a link and has no image, offer to attach a preview card. Off by default; turn on to be prompted.'
        forkAdded
      />
      <DesktopSettingsSwitch
        settingsKey='voiceRecordingOriginalAudio'
        label='Record without noise filtering'
        description="Your device normally removes background noise, cancels echo and evens out volume. That's good for speech, but it can flatten music, ambient sound or room tone. Turn this on to record what the microphone actually hears. Some browsers and microphones ignore this setting."
        forkAdded
        // The recorder only applies this while the custom player is on
        // (`experimental && originalAudio` in AudioRecorder); same
        // dependent-switch treatment as the voice-message rows in Notifications.
        disabled={
          !settingsStore?.desktopSettings['experimentalAudioPlayerControls']
        }
        disabledValue={false}
        callback={() => setMicTestRun(n => n + 1)}
      />
      {/* Flipping the switch invalidates a finished take: the clip was
          recorded under the old setting, and its verdict is read against the
          new one — record with filtering on, then switch on, and it would
          claim the browser "ignored the request" about a take that never made
          one. Bumping the key remounts, and the existing unmount cleanup stops
          any take in flight and revokes the object URL. */}
      <MicrophoneTest key={micTestRun} />
      <SettingsSeparator />
      <SettingsHeading>Chats</SettingsHeading>
      <DesktopSettingsSwitch
        settingsKey='experimentalUnreadReadTime'
        label='Estimated read time on unread badge'
        description='Show roughly how long the unread messages in a chat take to read (e.g. "~4 min") next to the unread counter. Off by default (experimental).'
        forkAdded
      />
      <DesktopSettingsSwitch
        settingsKey='enableOnDemandLocationStreaming'
        label={tx('pref_on_demand_location_streaming')}
        callback={value =>
          showExperimentalInfoDialog('enableOnDemandLocationStreaming', value)
        }
      />
      <DesktopSettingsSwitch
        settingsKey='experimentalAudioPlayerControls'
        label='Custom voice-message player'
        description='Custom controls for voice and audio messages: play/pause, seek bar, elapsed time, and playback speed. On by default; turn off if playback gives you trouble.'
        forkAdded
      />
      <SettingsSeparator />
      <SettingsHeading>System</SettingsHeading>
      {runtime.getRuntimeInfo().isContentProtectionSupported && (
        <DesktopSettingsSwitch
          settingsKey='contentProtectionEnabled'
          label={tx('pref_screen_security')}
          description={tx('pref_screen_security_explain')}
        />
      )}
      <SyncAllAccountsSwitch />
      <SettingsSelector
        onClick={() => openDialog(AppPickerUrlDialog)}
        currentValue={
          settingsStore == undefined
            ? undefined
            : settingsStore.desktopSettings.appStoreBaseUrl ||
              defaultAppStoreBaseUrl
        }
      >
        {tx('webxdc_store_url')}
      </SettingsSelector>
    </>
  )
}

export default function SyncAllAccountsSwitch() {
  const tx = useTranslationFunction()
  const settingsStore = useSettingsStore()[0]

  return (
    <SettingsSwitch
      label={tx('pref_background_sync_disabled')}
      description={tx('explain_background_sync_disabled')}
      value={settingsStore?.desktopSettings.syncAllAccounts !== true}
      disabled={settingsStore == null}
      onChange={() => {
        if (settingsStore == null) {
          return
        }
        SettingsStoreInstance.effect.setDesktopSetting(
          'syncAllAccounts',
          !settingsStore.desktopSettings.syncAllAccounts
        )
      }}
    />
  )
}

function AppPickerUrlDialog({ onClose }: DialogProps) {
  const tx = useTranslationFunction()
  const settingsStore = useSettingsStore()[0]

  return (
    <Dialog onClose={onClose}>
      <DialogHeader title={tx('webxdc_store_url')} onClose={onClose} />
      <form
        action={formData => {
          const url = formData.get('url')
          if (typeof url !== 'string') {
            log.error('App picker URL form submitted, but URL is', url)
            return
          }
          SettingsStoreInstance.effect.setDesktopSetting(
            'appStoreBaseUrl',
            url === '' ? undefined : url
          )
          onClose()
        }}
      >
        <DialogBody>
          <DialogContent>
            <p className='whitespace'>{tx('webxdc_store_url_explain')}</p>
            <p className='whitespace'>
              {tx('webxdc_store_url_explain_2_desktop')}
            </p>
            {settingsStore && (
              <DeltaInput
                value={undefined}
                placeholder={defaultAppStoreBaseUrl}
                onChange={() => {}}
                type='url'
                name='url'
                defaultValue={settingsStore.desktopSettings.appStoreBaseUrl}
              />
            )}
          </DialogContent>
        </DialogBody>
        <DialogFooter>
          <FooterActions>
            <FooterActionButton onClick={onClose}>
              {tx('cancel')}
            </FooterActionButton>
            <FooterActionButton type='submit' styling='primary'>
              {tx('save')}
            </FooterActionButton>
          </FooterActions>
        </DialogFooter>
      </form>
    </Dialog>
  )
}
